import { DefaultLogger, LogMessageType } from './Logger';
import { TFunction } from 'i18next';
import { Identity, OverridesResponse, SensitivityLabel } from '../Ge/types';
import { ApmBase, Transaction } from '@elastic/apm-rum';
import { AllRecipientTypes, TokenData, NativeLinksToUpdate } from '../Ge/types';
import { CLIENT_ID, GRAPH_ENDPOINT, initialErrorData, MS_AUTHORITY, GRAPH_SCOPE, SERVER_URL, HTTP_REQUEST_TIMEOUT_MS } from './constants';
import { BrowserAuthError, BrowserAuthErrorCodes, BrowserCacheLocation, Configuration, createNestablePublicClientApplication, InteractionRequiredAuthError, IPublicClientApplication, LogLevel } from '@azure/msal-browser';
import axios, { AxiosResponse } from 'axios';
import { DirectoryObject, Group } from "@microsoft/microsoft-graph-types";
import { pollTaskId } from './getUserInfo/pollTaskId';
import { jwtDecode, JwtPayload } from 'jwt-decode';

export const METADATA_DEST_FIELD = 'metadata.dest';
export const METADATA_HEADER_FIELD = 'metadata.header';
export const METADATA_VAL_FIELD = 'metadata.val';
export const METADATA_POLICY_FIELD = 'metadata.policy';

interface CWPJwtPayload extends JwtPayload {
  email?: string;
  identity?: string;
}

export enum RecipientType {
  TO = 'To',
  CC = 'Cc',
  BCC = 'Bcc'
};

export enum Scopes {
  GRAPH = "graph"
}

export interface DialogResult {
  type: Office.EventType,
  error?: number,
  message?: string
};

export interface OnSendNotification {
  success: boolean,
  message: any,//string | string[] | EmailAddress[],
  type?: string | RecipientType,
  headersResponse?: OverridesResponse
};

export interface GlobalInfrastructureServices {
  logger: DefaultLogger,
  outlookActions: OutlookActions<AllRecipientTypes>,
  t: TFunction
}

export class OutlookActions<TRecipientType extends AllRecipientTypes> {
  protected logger: DefaultLogger;
  protected messageChangeCb: (item?: any) => void = null;
  protected messageCustomProperties: Office.CustomProperties = null;
  protected headersSupported: boolean = null;
  protected retryCount: number;
  protected emailItem: Office.Item & Office.ItemCompose & Office.MessageCompose;
  public labelsCatalog: any[];
  private initialized: boolean = false;
  static apm: ApmBase;
  public transactionId: string;
  public traceId: string;
  protected transaction: Transaction;
  static publicClientApp: IPublicClientApplication;
  static NAAtoken: string;
  static NAAscope: Scopes;
  static msalLogs: Array<{level: LogLevel, message: string, timestamp: Date}> = [];
  static readonly MAX_LOG_ENTRIES = 500; // Limit log buffer size

  constructor(logger: DefaultLogger, apm: ApmBase) {
    this.logger = logger;
    OutlookActions.apm = apm;
  }

  public static async initPCA(): Promise<IPublicClientApplication> {
    if (!OutlookActions.publicClientApp) {
      const msalConfig: Configuration = {
        auth: {
          clientId: CLIENT_ID,
          authority: MS_AUTHORITY,
          redirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage,
          storeAuthStateInCookie: true,
        },
        system: {
          loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
              if (!containsPii) {
                if ((typeof __DEV__ !== "undefined") && __DEV__) {
                  console.log(`MSAL [${level}]: ${message}`);
                }

                OutlookActions.collectMsalLog(level, message);
              }
            },
            piiLoggingEnabled: false,
            logLevel: LogLevel.Verbose,
          },
        },
      };

      OutlookActions.publicClientApp = await createNestablePublicClientApplication(msalConfig);
    }

    return OutlookActions.publicClientApp;
  }

  public init(): boolean {
    if (this.initialized) {
      return true; // Already initialized, no need to do it again
    }

    if (Office.context.mailbox) {
      this.emailItem = Office.context.mailbox.item;
      if (this.emailItem) {
        if (this.emailItem.itemType === Office.MailboxEnums.ItemType.Message) {
          this.logger.logMessageWithType(LogMessageType.info, 'Item type is Message');
        }
      } else {
        this.logger.logMessageWithType(LogMessageType.info, 'Office.context.mailbox.item is empty');
      }
      this.initialized = true; // Mark as initialized
      return true;
    }

    return false;
  }

  /**
   * This function registers a callback to be triggered when we open the "send new message" pane
   * and the outlook plugin is already pinned.
   */
  public setMessageChangeCallback(cb: () => void) {
    if (Office.context.requirements.isSetSupported("Mailbox", '1.5')) {
      this.messageChangeCb = cb;
      Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, this.loadNewItem);
    }
    else {
      cb();
    }
  }

  /**
   * This function is called every time we open the "send new message" pane
   * and the outlook plugin is pinned. It's request any previously saved
   * custom props in Exchange and update its internal state.
   */
  protected loadNewItem = (_: Office.EventType) => {
    this.logger.logMessageWithType(LogMessageType.info, 'New message selected');
    this.emailItem = Office.context.mailbox.item;
    this.logger.logMessageWithType(LogMessageType.info, 'New message selected');

    if (this.messageChangeCb) {
      this.messageChangeCb();
    }
  }

  /**
   * Helper method to create error messages
   *
   * @param desc The description of the error message
   * @param status The outlook error code
   */
  protected createError(desc: string, status: Office.AsyncResult<any>): Error {
    if (status) {
      return new Error(desc + ' [' + status?.error?.code + ' ' + status?.error?.name + '] ' + status?.error?.message);
    }
    else {
      return new Error(desc);
    }
  }

  public async getNAAtoken(scope: Scopes): Promise<string> {
    if (OutlookActions.NAAtoken && OutlookActions.NAAscope === scope) {
      return OutlookActions.NAAtoken;
    }
    const apmSpan = OutlookActions.apm.startSpan(`getNAAtoken ${scope}`, 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );
    const msalInstance = await OutlookActions.initPCA();
    const accounts = msalInstance.getAllAccounts();
    const userEmail = Office.context.mailbox?.userProfile?.emailAddress || localStorage.getItem('emailAddress') || null;
    const activeAccount = msalInstance.getActiveAccount() || (accounts?.length > 0 ? accounts[0] : null);

    const tokenRequest = {
      scopes: GRAPH_SCOPE,
      account: activeAccount
    };

    let accessToken = null;
    let msalError = null;

    try {
      const userAccount = await msalInstance.acquireTokenSilent(tokenRequest);
      accessToken = userAccount.accessToken;
      this.logger.logMessageWithType(LogMessageType.info, 'Acquired token silently.');
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError ||
        (error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.noAccountError) ||
        (!accounts?.some(account => account.username === userEmail))) {
        // Only trigger the popup if silent acquisition fails

        try {
          const userAccount = await msalInstance.acquireTokenPopup(tokenRequest);
          accessToken = userAccount.accessToken;
        } catch (popupError) {
          msalError = popupError;
        }
      } else {
        msalError = error;
      }
    }

    OutlookActions.reportMSALLogs();
    apmSpan?.end();
    localStorage.removeItem('emailAddress');

    if (accessToken) {
      OutlookActions.NAAtoken = accessToken;
      OutlookActions.NAAscope = scope;
      return accessToken;
    } else {
      throw new Error(msalError?.message ?? 'Unable to acquire access token.');
    }
  }

  public async exchangeTokens(accessToken: string): Promise<TokenData> {
    const storedToken = localStorage.getItem('cwpToken');

    if (storedToken) {
      const parsedToken = JSON.parse(storedToken) as TokenData;
      if (new Date().getTime() + 60 * 1000 < parsedToken.expiration && parsedToken.emailAddress === Office.context.mailbox.userProfile.emailAddress) {
        return {
          token: parsedToken.token,
          expiration: parsedToken.expiration,
          emailAddress: parsedToken.emailAddress
        }
      }
    }

    // const host = __DEV__ ? '' : serverUrl;

    const exchangeTokensUrl = `${SERVER_URL}/oauth2/exchange/ms/jwt/`;
    let response: AxiosResponse<{ access_token: string; expire: string; }>;

    try {
      response = await axios.post<{
        access_token: string;
        expire: string;
      }>(
        exchangeTokensUrl,
        {},
        {
          // Axios defaults to 0 (wait forever). This call sits on the send-blocking path, so it must be
          // bounded or a stalled response wedges the send permanently (AB#14110).
          timeout: HTTP_REQUEST_TIMEOUT_MS,
          headers: {
            Authorization: `MSBEARER ${accessToken}`,
            // ...(__DEV__ ? { 'Eshare-CF-Client-Id': '2945ad93dba442d0b1dc52a43148b1ee' } : {}),
          },
        }
      );
    } catch (error) {
      // Axios's own timeout error carries no URL ("timeout of Xms exceeded"), so without this a human
      // scanning APM labels has to deduce which HTTP call timed out by elimination. Label it explicitly,
      // matching the `timed-out:<operation>` convention used for the Office.js call bounds (AB#14110).
      // The original error is rethrown unchanged - setToken()'s 404-onboarding branch inspects
      // error.response, which this must not disturb.
      if (error?.code === 'ECONNABORTED') {
        OutlookActions.apm?.getCurrentTransaction()?.addLabels({ "timed-out:exchangeTokens": true });
        OutlookActions.apm?.captureError(new Error(`exchangeTokens timed out after ${HTTP_REQUEST_TIMEOUT_MS}ms calling ${exchangeTokensUrl}`));
      }

      throw error;
    }

    const cwpToken = response.data.access_token;
    const cwpExpiration = new Date(response.data.expire).getTime();

    const decodedCwpToken = jwtDecode<CWPJwtPayload>(cwpToken);
    
    return {
      token: cwpToken,
      expiration: cwpExpiration,
      emailAddress: decodedCwpToken?.email || ''
    }
  }

  public async getUserInfo(accessToken: string, userOnly: boolean): Promise<Identity> {
    return new Promise<Identity>((resolve, reject) => {
      try {
        // const host = __DEV__ ? '' : SERVER_URL;

        pollTaskId<ReturnType<typeof axios.get>, [string, any]>(
          axios.get,
          [
            `${SERVER_URL}/api/3.0/identities/me-background/`,
            { 
              params: userOnly ? {
                only_user: true,
              } : {},
              headers: {
                Authorization: `MSBEARER ${accessToken}`,
                // ...(__DEV__ ? { 'Eshare-CF-Client-Id': '2945ad93dba442d0b1dc52a43148b1ee' } : {}),
              }
            },
          ],
          3000,
          true,
          undefined,
          () => {
            this.transaction?.addLabels({ "user server provision triggered": true });
          },
          {
            useV1Polling: true,
            tokenOverride: accessToken,
          }
        )
          .then((response) => {
            this.transaction?.addLabels({
              "user server provision successful": true,
              "identity": JSON.stringify(response)
            });
            resolve(response)
          })
          .catch((error) => {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Saves the message in the draft folder
   */
  public saveDraft(): Promise<string> {
    const apmSpan = OutlookActions.apm.startSpan('saveDraft', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item.saveAsync((result: Office.AsyncResult<string>) => {
        if (result.status == Office.AsyncResultStatus.Failed) {
          OutlookActions.apm.captureError(this.createError('Failed to save message, in order to retrieve the correct user token.', result));
          apmSpan?.end();
          reject(this.createError('Failed to save message, in order to retrieve the correct user token.', result));
        }

        OutlookActions.apm.addLabels({ draftId: result.value });
        apmSpan?.end();
        resolve(result.value);
      });
    });
  }

  public getHtmlBodyFromOutlook(): Promise<Office.AsyncResult<string>> {
    return new Promise((resolve, reject) => {
      this.emailItem.body.getAsync(Office.CoercionType.Html, {}, (asyncResult) => {
        if (!asyncResult || asyncResult.status == Office.AsyncResultStatus.Failed) {
          reject(this.createError('Failed to retrieve message body.', asyncResult));
        }

        resolve(asyncResult);
      });
    });
  }

  public setHtmlBodyToOutlook(body: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.emailItem.body.setAsync(body, { coercionType: Office.CoercionType.Html }, (asyncResult) => {
        if (!asyncResult || asyncResult.status == Office.AsyncResultStatus.Failed) {
          reject(this.createError('Failed to set message body.', asyncResult));
        }

        resolve();
      });
    });
  }

  public async setEmailBodyWithUpdatedLInks(links: NativeLinksToUpdate[]): Promise<void> {
      try {
        const currentBody = await this.getHtmlBodyFromOutlook();
        let bodyValue = currentBody.value;
        links.forEach(({ inserted_link, converted_to_named_link }) => {
          if (converted_to_named_link) {
            bodyValue = bodyValue.replace(inserted_link, converted_to_named_link)
          }
        })

        if (bodyValue === currentBody.value) return;
        
        await this.setHtmlBodyToOutlook(bodyValue);
      } catch (error) {
        OutlookActions.apm.captureError(new Error(`Failed to update links on email body. ${error instanceof Error ? error.message : String(error)}`));
    }
  }

  public getSubjectFromOutlook(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.emailItem.subject.getAsync((asyncResult) => {
        if (!asyncResult || asyncResult.status == Office.AsyncResultStatus.Failed) {
          OutlookActions.apm.captureError(this.createError('Failed to get message subject.', asyncResult));

          return reject(this.createError('Failed to retrieve message subject.', asyncResult));
        }
        this.logger.logMessageWithType(LogMessageType.info, 'Message subject retrieved successfully.');

        OutlookActions.apm.addLabels({ subject: asyncResult.value });
        resolve(asyncResult.value);
      });
    });
  }

  public setSubjectToOutlook(subject: string): Promise<void> {
    const apmSpan = OutlookActions.apm.startSpan('setSubjectToOutlook', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );

    return new Promise((resolve, reject) => {
      this.emailItem.subject.setAsync(subject, {}, (asyncResult) => {
        if (!asyncResult || asyncResult.status == Office.AsyncResultStatus.Failed) {
          OutlookActions.apm.captureError(this.createError('Failed to set message subject.', asyncResult));
          apmSpan?.end();

          return reject(this.createError('Failed to set message subject.', asyncResult));
        }

        OutlookActions.apm.addLabels({ "subject-set": subject });
        apmSpan?.end();
        resolve();
      });
    });
  }

  /**
   * Adds a header to the message
   *
   * @param headerName the name of the header to add
   * @param value the value of the header
   */
  public setHeaderToOutlook(headerName: string, value: string): Promise<void> {
    const apmSpan = OutlookActions.apm.startSpan('setHeaderToOutlook', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );
    let headerPair = {};
    headerPair[headerName] = value;

    return new Promise((resolve, reject) => {
      this.emailItem.internetHeaders.setAsync(headerPair, {}, (asyncResult) => {
        if (!asyncResult || asyncResult.status == Office.AsyncResultStatus.Failed) {
          OutlookActions.apm.captureError(this.createError('Failed to set message header.', asyncResult));
          apmSpan?.end();

          return reject(this.createError('Failed to set message header.', asyncResult));
        }

        OutlookActions.apm.addLabels({ header: headerName });
        apmSpan?.end();
        resolve();
      });
    });
  }

  /**
   * Adds bulk headers object to the message
   *
   * @param headersData the headers object to add
   */
  public setHeadersToOutlook(headersData: Record<string, string>): Promise<void> {
    const apmSpan = OutlookActions.apm.startSpan('setHeadersToOutlook', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );

    return new Promise((resolve, reject) => {
      this.emailItem.internetHeaders.setAsync(headersData, {}, (asyncResult) => {
        if (!asyncResult || asyncResult.status == Office.AsyncResultStatus.Failed) {
          OutlookActions.apm.captureError(this.createError('Failed to set message header.', asyncResult));
          apmSpan?.end();

          return reject(this.createError('Failed to set message header.', asyncResult));
        }

        OutlookActions.apm.addLabels({ additional_headers: JSON.stringify(headersData) });
        apmSpan?.end();
        resolve();
      });
    });
  }

  /**
   * Retrieves the requested recipient field (To, Cc, Bcc) from the current email item
   * @param recipientType
   */
  protected getRecipientField(recipientType: TRecipientType): Office.Recipients {
    switch (recipientType) {
      default:
      case RecipientType.TO:
        return this.emailItem.to;

      case RecipientType.CC:
        return this.emailItem.cc;

      case RecipientType.BCC:
        return this.emailItem.bcc;
    }
  }

  /**
   * Returns all the recipients from the requested field (to, cc, bcc)
   * @param recipientType the type of the recipients (to, cc, bcc)
   */
  public async getRecipients(recipientType: TRecipientType): Promise<Office.EmailAddressDetails[]> {
    const apmSpan = OutlookActions.apm.startSpan('getRecipients', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );
    return new Promise((resolve, reject) => {
      let recipientsField: Office.Recipients = this.getRecipientField(recipientType);

      recipientsField.getAsync({}, (asyncResult: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
        if (asyncResult.status == Office.AsyncResultStatus.Failed) {
          OutlookActions.apm.captureError(this.createError(`Failed to get ${recipientType}: recipients.`, asyncResult));
          reject(this.createError(`Failed to get ${recipientType}: recipients.`, asyncResult));
          return;
        }

        /*if (asyncResult.value && asyncResult.value.length > 0) {
          asyncResult.value.forEach(x => {
            this.logger.logMessageWithType(LogMessageType.info, `${x.displayName} ${x.emailAddress} ${x.recipientType}`);
          });
        }*/

        OutlookActions.apm.addLabels({ recipients: asyncResult?.value?.map(rec => rec?.emailAddress).join(",") });
        apmSpan?.end();
        resolve(asyncResult.value);
      });
    });
  }

  public async getSensitivityLabel(): Promise<any> {
    const itemId = await this.getItemIdAsync();
    const apmSpan = OutlookActions.apm.startSpan('getSensitivityLabel', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );
    let errorData = initialErrorData;
    return new Promise((resolve, reject) => {
      let labelId: string;

      if (!Office.context.requirements.isSetSupported('Mailbox', '1.13')) {
        // Labels are not supported in legacy Desktop Outlook, we let the email go.
        OutlookActions.apm.addLabels({ "labels-not-supported": true, "label-not-validated": true });
        apmSpan?.end();
        return resolve(undefined);
      }

      Office.context.sensitivityLabelsCatalog.getIsEnabledAsync((asyncResult) => {
        if (asyncResult?.status !== Office.AsyncResultStatus.Succeeded || asyncResult?.value !== true) {
          errorData = {
            ...errorData,
            customError: "GetIsEnabledAsync failed",
            asyncResultValue: asyncResult?.value,
            asyncResultStatus: asyncResult?.status,
            asyncErrorMessage: `message: ${asyncResult?.error?.message}, error code: ${asyncResult?.error?.code}`
          };

          OutlookActions.apm.addLabels({ "labels-not-enabled": true });
          let error = this.createError("Labels catalog not enabled", asyncResult);

          if (asyncResult?.status !== Office.AsyncResultStatus.Succeeded) {
            error = this.createError("GetIsEnabledAsync failed", asyncResult);
          }

          return reject(error);
        }

        Office.context.sensitivityLabelsCatalog.getAsync((catalogResult) => {
          if (catalogResult?.status !== Office.AsyncResultStatus.Succeeded) {
            errorData = {
              ...errorData,
              customError: "GetAsync Catalog failed",
              asyncResultValue: catalogResult?.value,
              asyncResultStatus: catalogResult?.status,
              asyncErrorMessage: `message: ${catalogResult?.error?.message}, error code: ${catalogResult?.error?.code}`
            };

            const error = this.createError("Catalog getAsync failed", catalogResult);

            return reject(error);
          }

          // Save the catalog in local storage for invocation from Dialog.
          localStorage.setItem(`catalog_${itemId}`, JSON.stringify(catalogResult?.value));
          this.labelsCatalog = catalogResult?.value;

          // Fetch the current email's sensitivity label
          this.emailItem?.sensitivityLabel.getAsync((labelResult) => {
            if (labelResult?.status !== Office.AsyncResultStatus.Succeeded) {
              errorData = {
                ...errorData,
                customError: "GetAsync email sensitivity label failed",
                asyncResultValue: labelResult?.value,
                asyncResultStatus: labelResult?.status,
                asyncErrorMessage: `message: ${labelResult?.error?.message}, error code: ${labelResult?.error?.code}`
              };

              const error = this.createError("GetAsync email sensitivity label failed", labelResult);

              return reject(error);
            }

            labelId = labelResult?.value;

            // no label set.
            if (labelId === "") {
              OutlookActions.apm.addLabels({ "label-not-set": true });
              apmSpan?.end();
              return resolve(undefined);
            }
            // Find the label by id from labelsCatalog, look in children as well.
            const labelInfo: SensitivityLabel = this.findLabelById(labelId, this.labelsCatalog);
            if (labelInfo) {
              OutlookActions.apm.addLabels({ "sensitivity-label": labelInfo?.name, "sensitivity-label-id": labelId });
              return resolve(labelInfo);
            } else {
              errorData = {
                customError: "Label not found in catalog",
                asyncResultValue: asyncResult?.value,
                asyncResultStatus: asyncResult?.status,
                labelId,
                catalog: this.labelsCatalog,
                asyncErrorMessage: undefined
              };

              const error = this.createError("Label not found in catalog", asyncResult);

              return reject(error);
            }
          });
        });
      });
    }).catch((e) => {
      OutlookActions.apm.getCurrentTransaction()?.addLabels({ error: e?.message });
      OutlookActions.apm.captureError(e);

      apmSpan?.end();

      return;
    });
  }

  public getItemClass(): Promise<string> {
    const apmSpan = OutlookActions.apm.startSpan('getItemClass', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );
    let errorData = initialErrorData;

    return new Promise((resolve) => {
      if (Office.context.requirements.isSetSupported('Mailbox', '1.14')) {
        this.emailItem.getItemClassAsync((asyncResult) => {
          if (asyncResult?.status !== Office.AsyncResultStatus.Succeeded) {
            errorData = {
              ...errorData,
              customError: "getItemClass failed",
              asyncResultValue: asyncResult?.value,
              asyncResultStatus: asyncResult?.status,
              asyncErrorMessage: `message: ${asyncResult?.error?.message}, error code: ${asyncResult?.error?.code}`
            };

            const error = this.createError('Failed to get item class while supported.', asyncResult);
            OutlookActions.apm.getCurrentTransaction()?.addLabels({ error: error?.message });
            OutlookActions.apm.captureError(error);

            apmSpan?.end();

            return resolve('');
          }

          OutlookActions.apm.addLabels({ itemClass: asyncResult?.value });
          apmSpan?.end();

          resolve(asyncResult.value);
        });
      } else {
        OutlookActions.apm.addLabels({ itemClass: "not supported" });
        apmSpan?.end();

        resolve('');
      }
    });
  }

  // Find a label by id in catalog
  public findLabelById = (labelId: string, labels: any): any | undefined => {
    for (const label of labels) {
      if (label.id === labelId) {
        return label;
      }

      if (label?.children?.length > 0) {
        const foundLabel = this.findLabelById(labelId, label.children);
        if (foundLabel) {
          return foundLabel;
        }
      }
    }
    return undefined;
  };

  private async getItemIdAsync(): Promise<string | undefined> {
    const apmSpan = OutlookActions.apm.startSpan('getItemIdAsync', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );
    return new Promise((resolve, reject) => {
      this.emailItem?.getItemIdAsync((itemIdResult) => {
        if (itemIdResult?.status !== Office.AsyncResultStatus.Succeeded) {
          OutlookActions.apm.captureError(this.createError('Failed to set sensitivity label.', itemIdResult));
          apmSpan?.end();
          return reject(this.createError('Failed to get Item Id.', itemIdResult));
        }

        apmSpan?.end();

        resolve(itemIdResult?.value);
      });
    });
  }

  public async setSensitivityLabel(labelId: string): Promise<void> {
    const itemId = await this.getItemIdAsync();
    const apmSpan = OutlookActions.apm.startSpan('setSensitivityLabel', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );
    let errorData = initialErrorData;
    return new Promise<void>((resolve) => {

      this.emailItem?.sensitivityLabel.setAsync(labelId, (asyncResult) => {
        if (asyncResult?.status !== Office.AsyncResultStatus.Succeeded) {
          errorData = {
            customError: "SenstitivityLabel setAsync failed",
            asyncResultValue: asyncResult?.value,
            asyncResultStatus: asyncResult?.status,
            labelId,
            catalog: localStorage.getItem(`catalog_${itemId}`),
            asyncErrorMessage: `message: ${asyncResult?.error?.message}, error code: ${asyncResult?.error?.code}`
          }
          OutlookActions.apm.captureError(JSON.stringify(errorData));
          OutlookActions.apm.captureError(this.createError('Failed to set sensitivity label.', asyncResult));
          apmSpan?.end();
        }

        OutlookActions.apm.addLabels({ "label-elevated": labelId });
        apmSpan?.end();

        resolve();
      });
    }).catch((error) => {
      let errorData = {
        customError: error?.message ?? "Something went wrong while setting elevated sensitivity label",
        asyncResultValue: undefined,
        asyncResultStatus: undefined,
        labelId,
        catalog: localStorage.getItem(`catalog_${itemId}`)
      };
      (error as any).data = errorData;

      OutlookActions.apm.captureError(new Error(`Failed to set sensitivity label. ${error instanceof Error ? error.message : String(error)}`));
      throw (error);
    })
  }

  public getEmailAttachments(): Promise<any[]> {
    const attachmentsSpan = OutlookActions.apm.startSpan('getEmailAttachments', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );
    return new Promise((resolve, reject) => {
      const item = Office.context.mailbox.item;

      item.getAttachmentsAsync((asyncResult) => {
        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          OutlookActions.apm.captureError(this.createError('Failed to get attachments.', asyncResult));
          attachmentsSpan?.end();
          return reject(this.createError('Failed to get attachments.', asyncResult));
        }

        const attachments = asyncResult?.value.map(attachment => {
          return {
            name: attachment.name,
            mimetype: attachment.attachmentType,
            size: attachment.size,
            is_inline: attachment.isInline
          };
        });

        OutlookActions.apm.setCustomContext({
          attachments: {
            summary: {
              total_count: asyncResult.value.length,
              total_size: asyncResult.value.reduce((sum, att) => sum + att.size, 0),
            },
            files: asyncResult.value.map((attachment) => ({
              filename: attachment.name,
              content_type: attachment.attachmentType,
              size_bytes: attachment.size,
              is_inline: attachment.isInline
            }))
          }
        });

        OutlookActions.apm.addLabels({ "physical files attached": attachments.length });

        attachmentsSpan?.end();

        resolve(attachments);
      });
    });
  }

  /**
   * Returns all the recipients from the requested field (to, cc, bcc)
   * @param recipientType the type of the recipients (to, cc, bcc)
   */
  public async setRecipients(recipientType: TRecipientType, recipients: string[] | Office.EmailAddressDetails[]): Promise<void> {
    const apmSpan = OutlookActions.apm.startSpan('setRecipients', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );

    return new Promise((resolve, reject) => {
      let recipientsField: Office.Recipients = this.getRecipientField(recipientType);

      recipientsField.setAsync(recipients, (asyncResult: Office.AsyncResult<void>) => {
        if (asyncResult.status == Office.AsyncResultStatus.Failed) {
          apmSpan?.end();

          return reject(this.createError(`Failed to set ${recipientType}: recipients.`, asyncResult));
        }
        apmSpan?.end();
        resolve();
      });
    });
  }

  /** 
   * Removes a recipient from the message.
   * @param recipientType the type of the recipient (to, cc, bcc)
   * @param recipientEmail the email address of the recipient
  */
  public async removeRecipient(recipientType: TRecipientType, recipientEmail: string, errorCallback?: (error: any) => void): Promise<void> {
    return new Promise((resolve, reject) => {
      let recipientsField: Office.Recipients = this.getRecipientField(recipientType);

      // Get the current recipients
      recipientsField.getAsync((asyncResult: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
        if (asyncResult.status == Office.AsyncResultStatus.Failed) {
          if (errorCallback) errorCallback(asyncResult.error);
          reject(asyncResult.error);
          return;
        }

        // Filter out the recipient to remove
        let updatedRecipients = asyncResult.value.filter(recipient => recipient.emailAddress !== recipientEmail);

        // Set the updated list of recipients
        recipientsField.setAsync(updatedRecipients, (setAsyncResult: Office.AsyncResult<void>) => {
          if (setAsyncResult.status == Office.AsyncResultStatus.Failed) {
            if (errorCallback) errorCallback(setAsyncResult.error);
            reject(setAsyncResult.error);
            return;
          }
          resolve();
        });
      });
    });
  }

  /**
   * Displays a dialog box and registers an event listener
   * @param url
   * @param msgHandler
   */
  public async showDialog(url: string, height: number, width: number, msgHandler: (result: DialogResult | { message: string; origin: string; } | { error: number; }) => void): Promise<Office.Dialog> {
    return new Promise((resolve, reject) => {

      Office.context.ui.displayDialogAsync(url, { height, width, displayInIframe: true },
        (asyncResult) => {
          if (!asyncResult || asyncResult.status == Office.AsyncResultStatus.Failed) {
            reject(this.createError('Failed to show dialog.', asyncResult));
            return;
          }

          let dialog = asyncResult.value;

          dialog.addEventHandler(Office.EventType.DialogEventReceived, msgHandler);
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, msgHandler);

          resolve(dialog);
        });
    });
  }

  public dialogEventHandler(result: DialogResult): boolean {
    if (result.type == Office.EventType.DialogEventReceived) {
      switch (result.error) {
        case 12002:
          throw 'The dialog box has been directed to a page that it cannot find or load, or the URL syntax is invalid.';
        case 12003:
          throw 'The dialog box has been directed to a URL with the HTTP protocol. HTTPS is required.';
        case 12006:
          this.logger.logMessageWithType(LogMessageType.info, 'Dialog closed.');
          break;
        default:
          throw `Unknown error (${result.error}) in dialog box.`;
      }

      return true;
    }

    return false;
  }

  /**
   * Sends a notification message to the dialog parent
   * @param notification
   */
  public sendParentNotification(notification: OnSendNotification) {
    Office.context.ui.messageParent(JSON.stringify(notification));
  }

  /**
   * Shows an inline notification message
   * @param key
   * @param type
   * @param message
   */
  public async setNotificationMessage(key: string, type: Office.MailboxEnums.ItemNotificationMessageType, message: string, icon?: string): Promise<void> {
    const apmSpan = OutlookActions.apm.startSpan('setNotificationMessage', 'outlook',
      {
        blocking: false,
        parentId: this.transactionId
      }
    );
    const notificationMessageDetails: Office.NotificationMessageDetails = {
      type,
      message,
      icon
    };

    if (!message || message.length == 0) {
      return new Promise((resolve, reject) => {
        this.emailItem?.notificationMessages.removeAsync(key, {}, (asyncResult: Office.AsyncResult<void>) => {
          if (!asyncResult || asyncResult.status == Office.AsyncResultStatus.Failed) {

            // 9024 is NotificationKeyNotFound, which happens if the user has removed the notification manually
            if (!asyncResult || asyncResult.error.code != 9024) {
              apmSpan?.end();
              reject(this.createError('Failed to remove notification.', asyncResult));
              return;
            }
          }
          apmSpan?.end();
          resolve();
        });
      });
    }

    if (type == Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage) {
      notificationMessageDetails.persistent = false;
    }

    return new Promise((resolve, reject) => {
      this.emailItem?.notificationMessages.replaceAsync(key, notificationMessageDetails, {}, (asyncResult: Office.AsyncResult<void>) => {
        if (!asyncResult || asyncResult.status == Office.AsyncResultStatus.Failed) {
          apmSpan?.end();
          reject(this.createError('Failed to set notification.', asyncResult));
          return;
        }
        apmSpan?.end();
        resolve();
      });
    });
  }

  public async parseEmailBodyForLinks(): Promise<string[]> {
    const links = [];

    try {
      const asyncResult = await this.getHtmlBodyFromOutlook();
      const emailBody = asyncResult.value;
      const sharePointLinkPattern = /href="(https:\/\/[^"]+\.sharepoint\.(com|us)\/[^"]*)"/g;

      let matches: RegExpExecArray | null;

      while ((matches = sharePointLinkPattern.exec(emailBody)) !== null) {
        links.push(matches[1]);
      }
    } catch (error) {
      OutlookActions.apm.captureError(new Error(`Failed to retrieve or parse email body. ${error instanceof Error ? error.message : String(error)}`));
    }

    return links;
  }

  public async resolveGraphDL(name: string, email: string): Promise<(DirectoryObject & { mail?: string })[] | null> {
    try {
      let group: Group | null = null;

      // Escape single quotes for OData string literals (the only OData-level escaping needed).
      // Note: names containing & or # may cause tier 1 to fail (they break the URL query string
      // since encodeURI() doesn't encode them), but tiers 2/3 use email which won't have these chars.
      const escapeOData = (val: string) => val.replace(/'/g, "''");

      // Tier 1: search by displayName (only if name is available)
      if (name && name.length > 0) {
        const escapedName = escapeOData(name);
        const groupByNameResponse = await this.makeGraphRequest(
          `${GRAPH_ENDPOINT}/v1.0/groups?$filter=displayName eq '${escapedName}'&$select=id,mail`
        );

        // If we got an email, validate the result by email as well or return the first result.
        group = email
          ? groupByNameResponse.data.value.find((group: Group) => group.mail?.toLowerCase() === email.toLowerCase()) ?? null
          : groupByNameResponse.data.value.length > 0 ? groupByNameResponse.data.value[0] : null;
      }

      // Fallback 1: if name-based lookup didn't find a match, try searching by primary email (mail property)
      if (!group && email && email.includes('@')) {
        const escapedEmail = escapeOData(email);
        const groupByEmailResponse = await this.makeGraphRequest(
          `${GRAPH_ENDPOINT}/v1.0/groups?$filter=mail eq '${escapedEmail}'&$select=id,mail`
        );
        group = groupByEmailResponse.data.value.length > 0 ? groupByEmailResponse.data.value[0] : null;
      }

      // Fallback 2: search by proxyAddresses — catches DLs where the `mail` attribute is null
      // (common for on-prem synced groups) or when the email is a secondary alias.
      // proxyAddresses stores all SMTP addresses: 'SMTP:primary@...' and 'smtp:alias@...'
      if (!group && email && email.includes('@')) {
        const escapedEmail = escapeOData(email);
        const groupByProxyResponse = await this.makeGraphRequest(
          `${GRAPH_ENDPOINT}/v1.0/groups?$filter=proxyAddresses/any(p:p eq 'SMTP:${escapedEmail}') or proxyAddresses/any(p:p eq 'smtp:${escapedEmail}')&$select=id,mail`
        );
        group = groupByProxyResponse.data.value.length > 0 ? groupByProxyResponse.data.value[0] : null;
      }

      if (!group) {
        return null;
      }

      const membersResponse = await this.makeGraphRequest(`${GRAPH_ENDPOINT}/v1.0/groups/${group.id}/members?$select=id,mail`);

      return membersResponse.data.value;
    } catch (error) {
      throw new Error(`Error resolving group members: ${error}`);
    }
  }

  public async makeGraphRequest(url: string, method?: string, payload?: any): Promise<AxiosResponse> {
    let token = await this.getNAAtoken(Scopes.GRAPH);

    try {
      const response = await axios({
        method: method || 'GET',
        url: encodeURI(url),
        // Axios defaults to 0 (wait forever). This resolves distribution-list members before the
        // validation dialog opens, on the same send-blocking path as the rest of AB#14110.
        timeout: HTTP_REQUEST_TIMEOUT_MS,
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Authorization': 'Bearer ' + token,
          'ConsistencyLevel': 'eventual'
        },
        data: payload
      });

      return response;
    } catch (error) {
      if (error?.code === 'ECONNABORTED') {
        OutlookActions.apm?.getCurrentTransaction()?.addLabels({ "timed-out:makeGraphRequest": true });
        OutlookActions.apm?.captureError(new Error(`makeGraphRequest timed out after ${HTTP_REQUEST_TIMEOUT_MS}ms calling ${url}`));
      }

      throw new Error(`Error making Graph request: ${error}`);
    }
  }

  public static reportMSALLogs(): void {
    if (OutlookActions.msalLogs.length === 0) {
      return;
    }
    
    try {
      // Format logs for APM
      const formattedLogs = OutlookActions.msalLogs.map(log => {
        return {
          message: log.message
          // timestamp: log.timestamp.toISOString(),
          // level: LogLevel[log.level], // Convert numeric level to string
        };
      });
      
      const errorLogs = OutlookActions.msalLogs.filter(log => log.level === LogLevel.Error);
      const warningLogs = OutlookActions.msalLogs.filter(log => log.level === LogLevel.Warning);
      const infoLogs = OutlookActions.msalLogs.filter(log => log.level === LogLevel.Info);
      
      // Summary information
      const logSummary = {
        totalCount: OutlookActions.msalLogs.length,
        errorCount: errorLogs.length,
        warningCount: warningLogs.length,
        infoCount: infoLogs.length,
        hasErrors: errorLogs.length > 0,
        hasWarnings: warningLogs.length > 0
      };
      
      // Common data to send to both systems
      const msalLogData = {
        msalLogs: formattedLogs,
        msalLogSummary: logSummary
      };
      // Send to APM as custom context
      OutlookActions.apm.setCustomContext(msalLogData);
      OutlookActions.apm.addLabels({
        'msal-log-count': OutlookActions.msalLogs.length,
        'msal-error-count': errorLogs.length,
        'msal-has-errors': errorLogs.length > 0 ? 'true' : 'false',
        'msal-has-warnings': warningLogs.length > 0 ? 'true' : 'false'
      });
    } catch (error) {
      OutlookActions?.apm.captureError(error);
    }
  }

  private static collectMsalLog(level: LogLevel, message: string): void {
    const logEntry = {
      level,
      message,
      timestamp: new Date()
    };
    
    // Add to log collection with size limit
    OutlookActions.msalLogs.push(logEntry);
    if (OutlookActions.msalLogs.length > OutlookActions.MAX_LOG_ENTRIES) {
      OutlookActions.msalLogs.shift(); // Remove oldest log
    }
  }
}

  // Local use of log dialog

//   public openOrUpdateLogsDialog(): void {
//     const logMessages = this.logger.getAllLogMessages();

//     if (!this.logDialog) {
//       const url = `https://${window.location.host}/debug-dialog`; // Replace with your actual URL
//       Office.context.ui.displayDialogAsync(url, { height: 50, width: 50 }, (result) => {
//         if (result.status === Office.AsyncResultStatus.Succeeded) {
//           this.logDialog = result.value;
//           this.logDialog.addEventHandler(Office.EventType.DialogMessageReceived, this.onDialogMessage);
//           this.sendLogsToDialog(logMessages);
//         } else {
//           this.logger.logMessageWithType(LogMessageType.error, `Failed to open logs dialog: ${result.error.message}`);
//         }
//       });
//     } else {
//       this.sendLogsToDialog(logMessages);
//     }
//   }

//   private sendLogsToDialog(logMessages: string[]): void {
//     if (this.logDialog) {
//       this.logDialog.sendMessage(JSON.stringify({ type: 'updateLogs', logs: logMessages }));
//     }
//   }

//   private onDialogMessage = (arg: any) => {
//     const message = JSON.parse(arg.message);
//     if (message.type === 'eraseLogs') {
//       this.logger.eraseLogs();
//       this.openOrUpdateLogsDialog(); // Refresh the dialog with empty logs
//     }
//   }
// }
